/**
 * Timeline Editor Component (canvas-v2)
 *
 * Main timeline component assembling ruler, playhead, and tracks.
 * Provides horizontal scrollable timeline view.
 *
 * V2 additions:
 * - Computes audioTrackCount from max trackIndex of audio clips
 * - Toolbar with type-aware Split/Delete/Copy/Paste/Undo/Redo buttons
 */

import { type ReactNode, useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Blend } from 'lucide-react'
import { TimelineRuler } from './TimelineRuler'
import { TimelinePlayhead } from './TimelinePlayhead'
import { TimelineTrack } from './TimelineTrack'
import { TrackHeaderColumn, TRACK_HEADER_COLUMN_DEFAULT_WIDTH, TRACK_HEADER_COLUMN_MIN, TRACK_HEADER_COLUMN_MAX, type TrackCell } from './TrackHeaderColumn'
import type { TimelineAudioClip, TimelineClip, TimelineClipOperationStateMap, TimelineState } from './types'
import { splitRangeForClip, splitTargetAtPlayhead } from './timelineSelection'
import { getAudioTrackLabelKey } from './timelineAudioKind'
import { createNadDebug } from '../../../../utils/debugLog'

// RFC-0630 v3 (architect-review #4 + low item #7): observability for the
// trim-cursor click-suppress. When the user trims a clip and the trailing
// pointerup → click lands on the timeline area, we early-return to keep
// the playhead from jumping. That early-return now emits a structured log
// line so the path is visible in NAD traces.
const trimCursorLog = createNadDebug('dw:video-editor:trim-cursor')
const splitFrameLog = createNadDebug('dw:video-editor:split-frame')
// RFC-0631 round-3 (Issue 3): emit a throttled scroll log so NAD traces
// can confirm the fixed-left header column does NOT translate when the
// timeline body scrolls horizontally.
const layoutLog = createNadDebug('dw:video-editor:layout')
const audioLabelLog = createNadDebug('dw:video-editor:audio-labels')

function clampToSplitRange(value: number, range: { min: number; max: number }) {
  return Math.max(range.min, Math.min(range.max, value))
}

function splitTraceId(clipId: string) {
  return `timeline-split:${clipId}`
}

interface TimelineEditorProps {
  leadingTracks?: { height: number; header: ReactNode; content: ReactNode }
  onTransitionChange?: (leftId: string, rightId: string, color: 'black' | 'white' | null) => void
  /** Timeline state (controlled by parent) */
  timelineState: TimelineState

  /** Replace selection with the given clip (non-shift click) */
  onSelectClip: (clipId: string | null, clipType?: 'video' | 'audio' | null) => void

  /** Toggle a clip's membership in the multi-selection (shift+click) */
  onToggleClipSelection?: (clipId: string) => void

  /** Clear the entire selection (e.g. clicking an empty area) */
  onClearSelection?: () => void

  /** Apply a uniform startOffset delta + optional track delta to ALL selected clips (multi-drag commit) */
  onMultiDragOffset?: (deltaSec: number, trackDelta?: number) => void

  /** Close-gaps callback (playhead-driven — closes every gap at or after the cursor on each track; does NOT delete clips). */
  onRippleDelete?: () => void

  /** Set playhead callback */
  onSetPlayhead: (position: number) => void

  /** Set zoom callback */
  onSetZoom: (zoomLevel: number) => void

  /** Reorder clip callback */
  onReorderClip: (clipId: string, newOrder: number) => void

  /** Video offset change callback */
  onClipOffsetChange?: (clipId: string, startOffset: number) => void

  /** Video track index change callback */
  onClipTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Inline video volume preview callback (no history push). */
  onClipVolumePreview?: (clipId: string, volume: number) => void

  /** Inline video volume commit callback (one history entry). */
  onClipVolumeCommit?: (clipId: string, volume: number, baselineVolume: number) => void

  /** Trim clip callback */
  onTrimClip: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Live trim preview callback (no history push) */
  onTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Audio offset change callback */
  onAudioOffsetChange?: (clipId: string, startOffset: number) => void

  /** Audio trim callback */
  onAudioTrimClip?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Live audio trim preview callback (no history push) */
  onAudioTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Audio track index change callback */
  onAudioTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Inline audio volume preview callback (no history push). */
  onAudioVolumePreview?: (clipId: string, volume: number) => void

  /** Inline audio volume commit callback (one history entry). */
  onAudioVolumeCommit?: (clipId: string, volume: number, baselineVolume: number) => void

  /** Called on mouseUp after a trim-start commit so any negative startOffset is normalized (Issue 1) */
  onNormalizeTimelineOrigin?: () => void

  /** Ref tracking playhead position at 60fps during playback */
  playheadPositionRef?: React.MutableRefObject<number>

  /** Whether timeline is read-only */
  readOnly?: boolean

  /** Callback to split the selected clip at playhead */
  onSplitSelectedClip?: () => void
  onCutAtPlayhead?: (side: 'left' | 'right') => void

  /** Callback to delete the selected clip */
  onDeleteSelectedClip?: () => void
  hasTextSelection?: boolean

  /** Callback to copy the selected clip */
  onCopyClip?: () => void

  /** Callback to paste the copied clip */
  onPasteClip?: () => void

  /** Callback to undo */
  onUndo?: () => void

  /** Callback to redo */
  onRedo?: () => void

  /** Whether undo is available */
  canUndo?: boolean

  /** Whether redo is available */
  canRedo?: boolean

  /** Add a new track */
  onAddTrack?: (type: 'video' | 'audio' | 'text') => void
  textTracksOnly?: boolean

  /**
   * RFC-0631 round-3 (Issue 5): Remove a sub-track. The `deleteClips`
   * argument is true only after the user accepted the confirm prompt
   * (when the sub-track was non-empty). The reducer in useTimelineState
   * still protects against removing track 0 (Main track) and against
   * dropping the track count below 1.
   */
  onRemoveTrack?: (
    trackKind: 'video' | 'audio',
    trackIndex: number,
    deleteClips: boolean,
  ) => void

  /** Drop a reusable left-panel reference on the timeline */
  onDropReference?: (
    clipId: string,
    clipType: 'video' | 'audio',
    startOffset: number,
    trackIndex: number
  ) => void

  /**
   * RFC-0651 #3: Main Track Magnet toggle. When provided, replaces the
   * one-shot "Close all gaps" toolbar button with a stateful toggle. The
   * actual pack-on-commit behavior is wired in TimelineEditorModal.
   */
  magnetEnabled?: boolean
  onSetMagnetEnabled?: (enabled: boolean) => void
  /**
   * RFC-0631 (Issue 3): Immediately pack track-0 gaps when the magnet toggle
   * flips ON. Without this, the magnet only takes effect on the next edit,
   * so the user has to "wiggle" a clip to see existing gaps close.
   */
  onMagnetPackTrack0?: () => void

  /** RFC-0651 #11: per-row track mute. */
  mutedVideoTracks?: Set<number>
  mutedAudioTracks?: Set<number>
  onToggleTrackMute?: (trackKind: 'video' | 'audio', trackIndex: number) => void

  /** Modal-local operation state, keyed by clip id. */
  operationStates?: TimelineClipOperationStateMap
}

const RULER_HEIGHT = 30
const VIDEO_TRACK_HEIGHT = 100
const AUDIO_TRACK_HEIGHT = 60
const MIN_TIMELINE_WIDTH = 1200

export function TimelineEditor({
  leadingTracks,
  onTransitionChange,
  timelineState,
  onSelectClip,
  onToggleClipSelection,
  onClearSelection,
  onMultiDragOffset,
  onRippleDelete: _onRippleDelete,
  onSetPlayhead,
  onSetZoom,
  onReorderClip,
  onClipOffsetChange,
  onClipTrackIndexChange,
  onClipVolumePreview,
  onClipVolumeCommit,
  onTrimClip,
  onTrimClipPreview,
  onAudioOffsetChange,
  onAudioTrimClip,
  onAudioTrimClipPreview,
  onAudioTrackIndexChange,
  onAudioVolumePreview,
  onAudioVolumeCommit,
  onNormalizeTimelineOrigin,
  playheadPositionRef,
  readOnly = false,
  onSplitSelectedClip,
  onCutAtPlayhead,
  onDeleteSelectedClip,
  hasTextSelection = false,
  onCopyClip,
  onPasteClip,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onAddTrack,
  textTracksOnly = false,
  onRemoveTrack,
  onDropReference,
  magnetEnabled = true,
  onSetMagnetEnabled,
  onMagnetPackTrack0,
  mutedVideoTracks,
  mutedAudioTracks,
  onToggleTrackMute,
  operationStates,
}: TimelineEditorProps) {
  const { t } = useTranslation('canvas')
  const verticalScrollWrapperRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const autoFollowRafRef = useRef<number | null>(null)
  const lastAutoFollowScrollMsRef = useRef(0)
  const [, setHorizontalScroll] = useState({ left: 0, max: 0 })
  const [snapPosition, setSnapPosition] = useState<number | null>(null)
  const [isTrackMenuOpen, setIsTrackMenuOpen] = useState(false)
  // RFC-0633 (Issue 3): user-resizable header column width. Persisted to
  // localStorage on pointerup; the splitter inside `TrackHeaderColumn` fires
  // live `onColumnWidthChange` on every pointermove for smooth dragging.
  const [headerColumnPx, setHeaderColumnPx] = useState<number>(() => {
    if (typeof window === 'undefined') return TRACK_HEADER_COLUMN_DEFAULT_WIDTH
    try {
      const raw = window.localStorage.getItem('dw:timeline:header-column-px')
      const parsed = raw === null ? NaN : Number(raw)
      if (Number.isFinite(parsed) && parsed >= TRACK_HEADER_COLUMN_MIN && parsed <= TRACK_HEADER_COLUMN_MAX) {
        return parsed
      }
    } catch {
      // ignore (e.g. cookies disabled)
    }
    return TRACK_HEADER_COLUMN_DEFAULT_WIDTH
  })
  const commitHeaderColumnPx = useCallback((next: number) => {
    setHeaderColumnPx(next)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('dw:timeline:header-column-px', String(next))
      } catch {
        // ignore
      }
    }
  }, [])

  const audioTrackCount = useMemo(() => {
    const maxTrackIndex = timelineState.audioClips.length > 0
      ? Math.max(...timelineState.audioClips.map((c) => c.trackIndex))
      : 0
    return Math.max(timelineState.audioTrackCount, maxTrackIndex + 1)
  }, [timelineState.audioClips, timelineState.audioTrackCount])

  const videoTrackCount = useMemo(() => {
    const maxTrackIndex = timelineState.clips.length > 0
      ? Math.max(...timelineState.clips.map((c) => c.trackIndex))
      : 0
    return Math.max(timelineState.videoTrackCount, maxTrackIndex + 1)
  }, [timelineState.clips, timelineState.videoTrackCount])

  // Handle timeline click (move playhead). Clicking empty timeline area
  // also clears the multi-selection so it doesn't persist when the user
  // clicks "off" the clips.
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      // Read-only editing still permits scrubbing and inspection.
      // RFC-0651 #2: when a trim drag was just released, the trailing click
      // event must NOT move the playhead. TimelineClip sets this flag on
      // pointerdown of a trim handle and clears it on a setTimeout(0) after
      // pointerup so this click handler sees it.
      try {
        if (window.__dwTimelineTrimActive) {
          trimCursorLog({
            event: 'playhead-click-suppressed',
            reason: 'trim-active',
          })
          return
        }
      } catch {
        // ignore (e.g. cross-origin)
      }

      const container = scrollContainerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const relativeX = e.clientX - rect.left + container.scrollLeft
      const time = relativeX / timelineState.zoomLevel

      const nextPosition = Math.max(0, Math.min(time, timelineState.totalDuration))
      if (playheadPositionRef) {
        playheadPositionRef.current = nextPosition
      }
      onSetPlayhead(nextPosition)
      // Empty-area click clears selection (clip-internal handlers stopPropagation)
      if (onClearSelection && !e.shiftKey) {
        onClearSelection()
      }
    },
    [timelineState.zoomLevel, timelineState.totalDuration, onSetPlayhead, playheadPositionRef, onClearSelection]
  )

  const handleClipClickPlayhead = useCallback(
    (clip: TimelineClip | TimelineAudioClip, clipType: 'video' | 'audio', requestedTime: number) => {
      const range = splitRangeForClip(clip, clipType)
      if (!range) {
        splitFrameLog({
          event: 'split-click-playhead-skipped',
          trace_id: splitTraceId(clip.id),
          reason: 'clip-too-short',
          clipId: clip.id,
          clipType,
          startOffsetS: Number(clip.startOffset.toFixed(3)),
          effectiveDurationS: Number(clip.effectiveDuration.toFixed(3)),
        })
        return
      }

      const nextPosition = clampToSplitRange(requestedTime, range)
      if (playheadPositionRef) {
        playheadPositionRef.current = nextPosition
      }
      onSetPlayhead(nextPosition)
      splitFrameLog({
        event: 'split-click-playhead-set',
        trace_id: splitTraceId(clip.id),
        clipId: clip.id,
        clipType,
        requestedTimeS: Number(requestedTime.toFixed(3)),
        playheadS: Number(nextPosition.toFixed(3)),
        rangeMinS: Number(range.min.toFixed(3)),
        rangeMaxS: Number(range.max.toFixed(3)),
      })
    },
    [onSetPlayhead, playheadPositionRef],
  )

  // Handle zoom
  const handleZoom = useCallback(
    (delta: number) => {
      const newZoom = Math.max(10, Math.min(200, timelineState.zoomLevel + delta))
      onSetZoom(newZoom)
    },
    [timelineState.zoomLevel, onSetZoom]
  )

  // Scroll playhead into view if needed. Keep auto-follow rAF-throttled with
  // edge hysteresis so playback does not fight the horizontal scrollbar.
  useEffect(() => {
    const cancelPendingAutoFollow = () => {
      if (autoFollowRafRef.current !== null) {
        window.cancelAnimationFrame(autoFollowRafRef.current)
        autoFollowRafRef.current = null
      }
    }

    if (!timelineState.isPlaying || !scrollContainerRef.current) {
      cancelPendingAutoFollow()
      return cancelPendingAutoFollow
    }
    if (autoFollowRafRef.current !== null) return cancelPendingAutoFollow

    autoFollowRafRef.current = window.requestAnimationFrame(() => {
      autoFollowRafRef.current = null
      const container = scrollContainerRef.current
      if (!container) return

      const playheadPixelPos = timelineState.playheadPosition * timelineState.zoomLevel
      const containerWidth = container.clientWidth
      const scrollLeft = container.scrollLeft
      const leftThreshold = scrollLeft + 96
      const rightThreshold = scrollLeft + containerWidth - 160
      if (playheadPixelPos >= leftThreshold && playheadPixelPos <= rightThreshold) return

      const now = Date.now()
      if (now - lastAutoFollowScrollMsRef.current < 250) return
      lastAutoFollowScrollMsRef.current = now
      const targetLeft = Math.max(0, playheadPixelPos - containerWidth * 0.35)
      container.scrollTo({ left: targetLeft, behavior: 'smooth' })
      layoutLog({
        event: 'playback-auto-follow-scroll',
        playheadPixel: Number(playheadPixelPos.toFixed(1)),
        fromScrollLeft: Number(scrollLeft.toFixed(1)),
        toScrollLeft: Number(targetLeft.toFixed(1)),
      })
    })
    return cancelPendingAutoFollow
  }, [timelineState.playheadPosition, timelineState.zoomLevel, timelineState.isPlaying])

  useEffect(() => () => {
    if (autoFollowRafRef.current !== null) {
      window.cancelAnimationFrame(autoFollowRafRef.current)
      autoFollowRafRef.current = null
    }
  }, [])

  const timelineWidth = Math.max(
    timelineState.totalDuration * timelineState.zoomLevel,
    MIN_TIMELINE_WIDTH
  )

  const playheadPixel = timelineState.playheadPosition * timelineState.zoomLevel

  const totalHeight = (leadingTracks?.height ?? 0) + RULER_HEIGHT + VIDEO_TRACK_HEIGHT * videoTrackCount + AUDIO_TRACK_HEIGHT * audioTrackCount

  // RFC-0631 round-3 (Issues 3 + 5 + 6): build the parallel cells +
  // heights arrays for TrackHeaderColumn. Video sub-tracks first, then
  // audio sub-tracks. clipCount drives the confirm prompt for Remove.
  const headerCells = useMemo<TrackCell[]>(() => {
    const out: TrackCell[] = []
    for (let i = 0; i < videoTrackCount; i++) {
      out.push({
        kind: 'video',
        trackIndex: i,
        clipCount: timelineState.clips.filter((c) => c.trackIndex === i).length,
      })
    }
    for (let i = 0; i < audioTrackCount; i++) {
      const trackAudioClips = timelineState.audioClips.filter((c) => c.trackIndex === i)
      const audioTrackLabelKey = getAudioTrackLabelKey(trackAudioClips)
      audioLabelLog({
        event: 'audio-track-label-resolved',
        feature: 'dw:video-editor:audio-labels',
        trackIndex: i,
        labelKey: audioTrackLabelKey,
        clipCount: trackAudioClips.length,
        audioKinds: trackAudioClips.map((clip) => clip.audioKind ?? 'legacy'),
      })
      out.push({
        kind: 'audio',
        trackIndex: i,
        labelKey: audioTrackLabelKey === 'audioTrack' ? undefined : audioTrackLabelKey,
        clipCount: trackAudioClips.length,
      })
    }
    return out
  }, [
    videoTrackCount,
    audioTrackCount,
    timelineState.clips,
    timelineState.audioClips,
  ])

  const headerCellHeights = useMemo(() => {
    const out: number[] = []
    for (let i = 0; i < videoTrackCount; i++) out.push(VIDEO_TRACK_HEIGHT)
    for (let i = 0; i < audioTrackCount; i++) out.push(AUDIO_TRACK_HEIGHT)
    return out
  }, [videoTrackCount, audioTrackCount])

  // RFC-0631 round-3 (Issue 3): throttle scroll-x logs to once per 500ms
  // so traces stay readable while still confirming the column doesn't move.
  const lastScrollLogRef = useRef(0)
  const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    // RFC-0635 regression guard: after the overflow-y-clip fix the body must
    // not own a vertical scroll context. If scrollTop ever leaks > 0 here, the
    // promotion rule has crept back (e.g. someone reverted to overflow-y-visible
    // alongside overflow-x-auto) and the wheel sync is broken again.
    if (target.scrollTop !== 0) {
      layoutLog({ event: 'body-scroll-y-leaked', scrollTop: target.scrollTop })
    }
    const now = Date.now()
    if (now - lastScrollLogRef.current < 500) return
    lastScrollLogRef.current = now
    layoutLog({
      event: 'scroll-x-detected',
      scrollLeft: target.scrollLeft,
      scrollTop: target.scrollTop,
      headerColumnPx,
    })
  }, [headerColumnPx])

  // RFC-0634 (header-track scroll sync): the outer wrapper now owns the
  // shared vertical scroll context so the left header column and the right
  // track body translate together. Throttled to 500ms (same pattern as the
  // horizontal scroll-x-detected log) so NAD traces stay readable.
  const lastScrollYLogRef = useRef(0)
  const handleWrapperScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const now = Date.now()
    if (now - lastScrollYLogRef.current < 500) return
    lastScrollYLogRef.current = now
    const target = e.currentTarget
    layoutLog({
      event: 'scroll-y-sync',
      scrollTop: target.scrollTop,
      columnPx: headerColumnPx,
    })
  }, [headerColumnPx])

  const syncHorizontalScrollState = useCallback(() => {
    const body = scrollContainerRef.current
    if (!body) return
    const max = Math.max(0, body.scrollWidth - body.clientWidth)
    const left = Math.min(max, Math.max(0, body.scrollLeft))
    setHorizontalScroll((prev) => {
      if (Math.abs(prev.left - left) < 0.5 && Math.abs(prev.max - max) < 0.5) {
        return prev
      }
      return { left, max }
    })
  }, [])

  const setTimelineScrollLeft = useCallback((left: number) => {
    const body = scrollContainerRef.current
    if (!body) return
    const max = Math.max(0, body.scrollWidth - body.clientWidth)
    body.scrollLeft = Math.min(max, Math.max(0, left))
    syncHorizontalScrollState()
  }, [syncHorizontalScrollState])

  useEffect(() => {
    syncHorizontalScrollState()
    const body = scrollContainerRef.current
    if (!body || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(syncHorizontalScrollState)
    observer.observe(body)
    if (body.firstElementChild instanceof HTMLElement) {
      observer.observe(body.firstElementChild)
    }
    return () => observer.disconnect()
  }, [syncHorizontalScrollState, timelineWidth, totalHeight])

  const lastBodyWheelLogRef = useRef(0)
  const handleBodyWheel = useCallback((e: WheelEvent) => {
    const wrapper = verticalScrollWrapperRef.current
    const body = scrollContainerRef.current
    if (!wrapper || !body) return
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? body.clientWidth : 1
    const dx = e.deltaX * unit, dy = e.deltaY * unit
    if (!dx && !dy) return
    // SHORTCUT_ID: timeline.wheelZoom
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      e.stopPropagation()
      const x = e.clientX - body.getBoundingClientRect().left
      const time = (body.scrollLeft + x) / timelineState.zoomLevel
      const zoom = Math.max(10, Math.min(200, timelineState.zoomLevel * Math.exp(-dy * 0.01)))
      onSetZoom(zoom)
      requestAnimationFrame(() => setTimelineScrollLeft(time * zoom - x))
      return
    }
    const beforeTop = wrapper.scrollTop
    const beforeLeft = body.scrollLeft
    // SHORTCUT_ID: timeline.wheelScroll
    if (e.shiftKey) wrapper.scrollTop += dy || dx
    else body.scrollLeft += Math.abs(dx) > Math.abs(dy) ? dx : dy
    const movedY = wrapper.scrollTop !== beforeTop
    const movedX = body.scrollLeft !== beforeLeft
    if (!movedY && !movedX) return
    if (movedX) syncHorizontalScrollState()

    e.preventDefault()
    e.stopPropagation()

    const now = Date.now()
    if (now - lastBodyWheelLogRef.current < 500) return
    lastBodyWheelLogRef.current = now
    layoutLog({
      event: 'body-wheel-forwarded',
      deltaX: Number(e.deltaX.toFixed(1)),
      deltaY: Number(e.deltaY.toFixed(1)),
      fromScrollTop: beforeTop,
      toScrollTop: wrapper.scrollTop,
      fromScrollLeft: beforeLeft,
      toScrollLeft: body.scrollLeft,
    })
  }, [syncHorizontalScrollState, setTimelineScrollLeft, timelineState.zoomLevel, onSetZoom])

  useEffect(() => {
    const body = scrollContainerRef.current
    if (!body) return

    body.addEventListener('wheel', handleBodyWheel, { passive: false })
    return () => {
      body.removeEventListener('wheel', handleBodyWheel)
    }
  }, [handleBodyWheel])

  // Type-aware toolbar helpers
  const selectionSize = timelineState.selectedClipIds.size
  const hasSelection = selectionSize > 0
  const isMultiSelected = selectionSize > 1
  const hasCopied = timelineState.copiedClip !== null
  const selectedOperationRunning = Array.from(timelineState.selectedClipIds).some(
    (clipId) => operationStates?.[clipId]?.status === 'running',
  )
  const editControlsDisabled = readOnly || selectedOperationRunning

  const selectedSplitTarget = splitTargetAtPlayhead(timelineState)
  const canSplitSelection = selectedSplitTarget !== null

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-card">
      {/* Toolbar: Zoom controls + editing actions */}
      <div className="flex items-center gap-2 px-4 py-2 bg-card-hover border-b border-border">
      {!readOnly && onAddTrack && (
        <div className="relative shrink-0">
          <button
            type="button"
            className="h-7 w-7 rounded-lg bg-surface-1 border border-input-border hover:bg-surface-3 hover:border-input-border-hover text-foreground flex items-center justify-center transition-colors"
            onClick={() => setIsTrackMenuOpen((open) => !open)}
            data-testid="timeline-add-track-button"
            title={t('timelineEditor.addTrack')}
          >
            <PlusIcon />
          </button>
          {isTrackMenuOpen && (
            <div className="absolute left-0 top-9 z-50 rounded-lg border border-border bg-background shadow-xl p-1 flex flex-col gap-1 min-w-max">
              <button type="button" className="h-7 rounded-lg px-2 text-xs font-medium text-foreground hover:bg-surface-2"
                onClick={() => { onAddTrack('text'); setIsTrackMenuOpen(false) }} data-testid="timeline-add-text-track">{t('textTrack.track')}</button>
              {!textTracksOnly && <><button
                type="button"
                className="h-7 rounded-lg px-2 text-xs font-medium text-foreground hover:bg-surface-2"
                onClick={() => {
                  onAddTrack('video')
                  setIsTrackMenuOpen(false)
                }}
                data-testid="timeline-add-video-track"
              >
                {t('timelineEditor.videoTrack')}
              </button>
              <button
                type="button"
                className="h-7 rounded-lg px-2 text-xs font-medium text-foreground hover:bg-surface-2"
                onClick={() => {
                  onAddTrack('audio')
                  setIsTrackMenuOpen(false)
                }}
                data-testid="timeline-add-audio-track"
              >
                {t('timelineEditor.audioTrack')}
              </button></>}
            </div>
          )}
        </div>
      )}


        {/* Zoom controls */}
        <span className="text-text-secondary text-sm">{t('timelineEditor.zoom')}</span>
        <input
          type="range"
          min="10"
          max="200"
          value={timelineState.zoomLevel}
          onChange={(e) => onSetZoom(Number(e.target.value))}
          className="w-24"
        />
        <span
          className="text-text-secondary text-sm min-w-[3ch]"
          data-testid="timeline-zoom-display"
          data-zoom-level={timelineState.zoomLevel}
        >
          {Math.round((timelineState.zoomLevel / 50) * 100)}%
        </span>
        <button
          onClick={() => handleZoom(10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
        >
          +
        </button>
        <button
          onClick={() => handleZoom(-10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
        >
          -
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-border mx-1" />

        {/* Editing toolbar actions */}
        {!readOnly && (
          <>
            <ToolbarButton
              label={t('timelineEditor.split')}
              icon={<SplitIcon />}
              iconOnly
              onClick={onSplitSelectedClip}
              disabled={editControlsDisabled || !canSplitSelection || !onSplitSelectedClip}
              title={t('timelineEditor.split')}
              testid="timeline-split-button"
            />
            {(['left', 'right'] satisfies ('left' | 'right')[]).map((side) => <ToolbarButton key={side} iconOnly
              label={t(`timelineCurves.delete_${side}`, side === 'left' ? 'Delete left' : 'Delete right')}
              title={t(`timelineCurves.delete_${side}`, side === 'left' ? 'Delete left' : 'Delete right')}
              icon={<SplitIcon deleted={side} />} onClick={() => onCutAtPlayhead?.(side)}
              disabled={editControlsDisabled || !canSplitSelection || !onCutAtPlayhead} testid={`timeline-delete-${side}`} />)}
            <ToolbarButton
              label={t('timelineEditor.delete')}
              icon={<DeleteIcon />}
              onClick={onDeleteSelectedClip}
              disabled={editControlsDisabled || !(hasSelection || hasTextSelection) || !onDeleteSelectedClip}
              testid="timeline-delete-button"
              title={hasTextSelection ? t('textTrack.delete') : hasSelection
                ? t('timelineEditor.deleteSelectedClips', { count: selectionSize })
                : t('timelineEditor.selectClipToDelete')
              }
            />
            <ToolbarToggle
              label={t('timelineEditor.mainTrackMagnet')}
              icon={<CloseAllGapsIcon />}
              pressed={magnetEnabled}
              onClick={() => {
                // RFC-0631 (Issue 3): When flipping ON, pack track-0 gaps
                // immediately so the user sees instant feedback rather than
                // having to make a follow-up edit.
                const next = !magnetEnabled
                onSetMagnetEnabled?.(next)
                if (next) onMagnetPackTrack0?.()
              }}
              disabled={!onSetMagnetEnabled}
              title={
                magnetEnabled
                  ? t('timelineEditor.magnetOnTitle')
                  : t('timelineEditor.magnetOffTitle')
              }
              testid="timeline-magnet-toggle"
            />
            <ToolbarButton
              label={t('timelineEditor.copy')}
              icon={<CopyIcon />}
              onClick={onCopyClip}
              disabled={!hasSelection || isMultiSelected || !onCopyClip}
              title={
                isMultiSelected
                  ? t('timelineEditor.multiClipCopyNotSupported')
                  : t('timelineEditor.copySelectedClip')
              }
            />
            <ToolbarButton
              label={t('timelineEditor.paste')}
              icon={<PasteIcon />}
              onClick={onPasteClip}
              disabled={editControlsDisabled || !hasCopied}
              title={hasCopied
                ? t('timelineEditor.pasteClip', { type: timelineState.copiedClipType || '' })
                : t('timelineEditor.copyClipFirst')
              }
            />

            {/* Separator */}
            <div className="w-px h-5 bg-border mx-1" />

            <ToolbarButton
              label={t('timelineEditor.undoLabel')}
              icon={<UndoIcon />}
              onClick={onUndo}
              disabled={editControlsDisabled || !canUndo}
              title={t('timelineEditor.undo')}
            />
            <ToolbarButton
              label={t('timelineEditor.redoLabel')}
              icon={<RedoIcon />}
              onClick={onRedo}
              disabled={editControlsDisabled || !canRedo}
              title={t('timelineEditor.redo')}
            />
          </>
        )}

        {/* Playhead time display */}
        <div className="ml-auto text-foreground text-sm font-mono" data-testid="timeline-time-display">
          {formatTime(timelineState.playheadPosition)} / {formatTime(timelineState.totalDuration)}
        </div>
      </div>

      {/* Scrollable timeline area — split into [fixed header | scrolling body]
          (RFC-0631 round-3 Issue 3) + shared vertical scroll context
          (RFC-0634: header-track scroll sync). The header column stays put
          on horizontal scroll; the body owns its own overflow-x. Both
          share the same vertical scroll because the outer wrapper now
          owns overflow-y. */}
      <div
        ref={verticalScrollWrapperRef}
        className="flex-1 flex overflow-y-auto overflow-x-hidden nowheel nodrag"
        onScroll={handleWrapperScroll}
        data-testid="timeline-scroll-wrapper"
      >
        <TrackHeaderColumn
          leadingHeader={leadingTracks?.header}
          cells={headerCells}
          cellHeights={headerCellHeights}
          rulerHeight={RULER_HEIGHT}
          mutedVideoTracks={mutedVideoTracks}
          mutedAudioTracks={mutedAudioTracks}
          onToggleTrackMute={onToggleTrackMute}
          onRemoveTrack={onRemoveTrack}
          locked={readOnly}
          columnPx={headerColumnPx}
          onColumnWidthChange={setHeaderColumnPx}
          onColumnWidthCommit={commitHeaderColumnPx}
        />
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto overflow-y-clip bg-canvas nowheel nodrag"
          style={{ minHeight: `${totalHeight}px` }}
          onScroll={(event) => {
            handleBodyScroll(event)
            syncHorizontalScrollState()
          }}
          data-testid="timeline-scroll-body"
        >
          <div
            className="relative bg-canvas"
            style={{ width: `${timelineWidth}px`, height: `${totalHeight}px` }}
            onClick={handleTimelineClick}
            data-testid="timeline-scroll-content"
          >
            {/* Ruler */}
            <div className="sticky top-0 z-20">
              <TimelineRuler
                totalDuration={timelineState.totalDuration}
                zoomLevel={timelineState.zoomLevel}
                viewportWidth={MIN_TIMELINE_WIDTH}
                height={RULER_HEIGHT}
              />
            </div>

            {leadingTracks?.content}
            {/* Video track */}
            <TimelineTrack
              type="video"
              label="Video"
              clips={timelineState.clips}
              zoomLevel={timelineState.zoomLevel}
              totalDuration={timelineState.totalDuration}
              selectedClipIds={timelineState.selectedClipIds}
              onSelectClip={(id) => onSelectClip(id, 'video')}
              onToggleClipSelection={onToggleClipSelection}
              onMultiDragOffset={onMultiDragOffset}
              onReorderClip={onReorderClip}
              onClipOffsetChange={onClipOffsetChange}
              onClipTrackIndexChange={onClipTrackIndexChange}
              onClipVolumePreview={onClipVolumePreview}
              onClipVolumeCommit={onClipVolumeCommit}
              onTrimClip={onTrimClip}
              onTrimClipPreview={onTrimClipPreview}
              onSnapPositionChange={setSnapPosition}
              onNormalizeTimelineOrigin={onNormalizeTimelineOrigin}
              playheadPixel={playheadPixel}
              onSetPlayheadFromClipClick={handleClipClickPlayhead}
              height={VIDEO_TRACK_HEIGHT}
              videoTrackCount={videoTrackCount}
              locked={readOnly}
              minWidth={MIN_TIMELINE_WIDTH}
              onDropReference={onDropReference}
              operationStates={operationStates}
            />

            {/* Audio tracks */}
            {!readOnly && onTransitionChange && timelineState.clips.map((left) => {
              const right = timelineState.clips.find((clip) => clip.id !== left.id && clip.trackIndex === left.trackIndex
                && Math.abs(clip.startOffset - (left.startOffset + left.effectiveDuration)) < 0.001)
              if (!right) return null
              const value = left.fadeOutS && right.fadeInS && left.fadeColor === right.fadeColor ? left.fadeColor ?? 'black' : 'none'
              return <label key={left.id} className="absolute z-10 flex h-6 w-6 items-center justify-center rounded border border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 focus-within:ring-2 focus-within:ring-primary"
                style={{ left: right.startOffset * timelineState.zoomLevel - 12, top: RULER_HEIGHT + (leadingTracks?.height ?? 0) + left.trackIndex * VIDEO_TRACK_HEIGHT + 4 }}
                title={t('timelineOverhaul.transition')} onClick={(event) => event.stopPropagation()}>
                <Blend className={`pointer-events-none h-3 w-3 ${value === 'none' ? '' : 'text-primary'}`} />
                <select aria-label={`${t('timelineOverhaul.transition')}: ${left.label} → ${right.label}`}
                  data-testid="timeline-transition" className="absolute inset-0 cursor-pointer opacity-0" value={value}
                  onChange={(event) => {
                    const color = event.target.value
                    if (color !== 'none' && color !== 'black' && color !== 'white') throw new Error('Invalid transition')
                    onTransitionChange(left.id, right.id, color === 'none' ? null : color)
                  }}>
                  <option value="none">{t('timelineOverhaul.cut')}</option>
                  <option value="black">{t('timelineOverhaul.dipBlack')}</option>
                  <option value="white">{t('timelineOverhaul.dipWhite')}</option>
                </select>
              </label>
            })}

            <TimelineTrack
              type="audio"
              label="Audio"
              clips={timelineState.audioClips}
              zoomLevel={timelineState.zoomLevel}
              totalDuration={timelineState.totalDuration}
              selectedClipIds={timelineState.selectedClipIds}
              onSelectClip={(id) => onSelectClip(id, 'audio')}
              onToggleClipSelection={onToggleClipSelection}
              onMultiDragOffset={onMultiDragOffset}
              onTrimClip={onTrimClip}
              onAudioTrimClip={onAudioTrimClip}
              onAudioTrimClipPreview={onAudioTrimClipPreview}
              onAudioOffsetChange={onAudioOffsetChange}
              onAudioTrackIndexChange={onAudioTrackIndexChange}
              onAudioVolumePreview={onAudioVolumePreview}
              onAudioVolumeCommit={onAudioVolumeCommit}
              audioTrackCount={audioTrackCount}
              height={AUDIO_TRACK_HEIGHT}
              locked={readOnly}
              minWidth={MIN_TIMELINE_WIDTH}
              onDropReference={onDropReference}
              onSetPlayheadFromClipClick={handleClipClickPlayhead}
              operationStates={operationStates}
            />

            {/* Playhead (overlays all tracks) */}
            <TimelinePlayhead
              position={timelineState.playheadPosition}
              positionRef={playheadPositionRef}
              totalDuration={timelineState.totalDuration}
              zoomLevel={timelineState.zoomLevel}
              height={totalHeight}
              onPositionChange={onSetPlayhead}
              isPlaying={timelineState.isPlaying}
            />

            {/* Snap indicator during drag or trim. */}
            {snapPosition !== null && (
              <div
                className="absolute top-0 w-0.5 bg-primary pointer-events-none z-30 animate-pulse shadow-lg"
                style={{
                  left: `${snapPosition}px`,
                  height: `${totalHeight}px`,
                }}
                data-testid="timeline-snap-indicator"
              />
            )}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {timelineState.clips.length === 0 && !(timelineState.textOverlays?.length) && !leadingTracks?.height && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-text-muted text-lg font-medium mb-2">{t('timelineEditor.noClipsOnTimeline')}</div>
            <div className="text-foreground/30 text-sm">
              {t('timelineEditor.connectVideoNodesToAdd')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---- Toolbar button ---- */

interface ToolbarButtonProps {
  iconOnly?: boolean
  label: string
  icon: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  title?: string
  testid?: string
}

function ToolbarButton({ label, icon, onClick, disabled, title, testid, iconOnly }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      data-testid={testid}
      className={`
        flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors focus-visible:ring-2 focus-visible:ring-primary
        ${disabled
          ? 'text-text-muted cursor-not-allowed'
          : 'text-text-secondary hover:bg-canvas hover:text-foreground'
        }
      `}
    >
      {icon}
      {!iconOnly && <span className="hidden sm:inline">{label}</span>}
    </button>
  )
}

/* ---- Toolbar toggle (RFC-0651 #3) ---- */

interface ToolbarToggleProps extends ToolbarButtonProps {
  pressed: boolean
}

function ToolbarToggle({
  label,
  icon,
  onClick,
  disabled,
  title,
  testid,
  pressed,
}: ToolbarToggleProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      aria-pressed={pressed}
      className={`
        flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors
        ${disabled
          ? 'text-text-muted cursor-not-allowed'
          : pressed
            ? 'bg-primary/15 text-primary'
            : 'text-text-secondary hover:bg-canvas hover:text-foreground'
        }
      `}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

/* ---- Icons (inline SVG) ---- */

function SplitIcon({ deleted }: { deleted?: 'left' | 'right' }) {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h5v16H4" strokeDasharray={deleted === 'left' ? '2 4' : undefined} opacity={deleted === 'left' ? .4 : 1} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 4h-5v16h5" strokeDasharray={deleted === 'right' ? '2 4' : undefined} opacity={deleted === 'right' ? .4 : 1} />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

function CloseAllGapsIcon() {
  // Collapse arrows: two vertical bars with arrows pointing inward
  // |→  ←|  — universal "close all gaps" / "collapse" symbol
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      data-testid="timeline-close-all-gaps-icon"
    >
      {/* Left bar */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5v14" />
      {/* Right bar */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 5v14" />
      {/* Left arrow pointing right */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h6m0 0l-2.5-2.5M12 12l-2.5 2.5" />
      {/* Right arrow pointing left */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12h-6m0 0l2.5-2.5M12 12l2.5 2.5" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function PasteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  )
}

function UndoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4 4M3 10l4-4" />
    </svg>
  )
}

function RedoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4 4M21 10l-4-4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m7-7H5" />
    </svg>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
